// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { getTransactions, getBanks } from "../lib/api";
import { toast } from "sonner";
import { Receipt, Search, ArrowRight } from "lucide-react";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const StatusBadge = ({ status }) => {
  const statusStyles = {
    success: "status-success",
    failed: "status-error",
    pending: "status-warning"
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusStyles[status] || 'status-neutral'}`}>
      {status}
    </span>
  );
};

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterIssuer, setFilterIssuer] = useState("all");
  const [filterAcquirer, setFilterAcquirer] = useState("all");

  const fetchData = async () => {
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterIssuer !== "all") params.issuer_bank_code = filterIssuer;
      if (filterAcquirer !== "all") params.acquirer_bank_code = filterAcquirer;
      
      const [txRes, banksRes] = await Promise.all([
        getTransactions(params),
        getBanks()
      ]);
      setTransactions(txRes.data);
      setBanks(banksRes.data);
    } catch (error) {
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterIssuer, filterAcquirer]);

  const filteredTx = transactions.filter(tx => {
    const query = searchQuery.toLowerCase();
    const authCode = tx.authorization_code || tx.voucher_reference || '';
    const walletRef = tx.wallet_reference || tx.voucher_reference || '';
    const txId = tx.id || '';
    
    return authCode.toLowerCase().includes(query) ||
           walletRef.toLowerCase().includes(query) ||
           txId.toLowerCase().includes(query);
  });

  // Calculate summary stats
  const totalVolume = filteredTx.reduce((sum, tx) => sum + (tx.status === 'success' ? tx.dispensed_amount : 0), 0);
  const totalFees = filteredTx.reduce((sum, tx) => sum + (tx.status === 'success' ? tx.fee_applied : 0), 0);
  const successCount = filteredTx.filter(tx => tx.status === 'success').length;

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl">Withdrawal Entries</h1>
          <p className="text-muted-foreground">View all ATM withdrawal entries</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Transactions</p>
            <p className="text-2xl font-bold font-mono mt-1">{filteredTx.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Successful</p>
            <p className="text-2xl font-bold font-mono mt-1 text-green-400">{successCount}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Volume</p>
            <p className="text-2xl font-bold font-mono mt-1">{formatCurrency(totalVolume)}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Fees</p>
            <p className="text-2xl font-bold font-mono mt-1 text-accent">{formatCurrency(totalFees)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search transactions..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-tx-input"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="filter-status-select">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterIssuer} onValueChange={setFilterIssuer}>
              <SelectTrigger data-testid="filter-issuer-select">
                <SelectValue placeholder="Issuer Bank" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Issuers</SelectItem>
                {banks.filter(b => b.bank_type === "issuer" || b.bank_type === "both").map(bank => (
                  <SelectItem key={bank.id} value={bank.bank_code}>{bank.bank_code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterAcquirer} onValueChange={setFilterAcquirer}>
              <SelectTrigger data-testid="filter-acquirer-select">
                <SelectValue placeholder="Acquirer Bank" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Acquirers</SelectItem>
                {banks.filter(b => b.bank_type === "acquirer" || b.bank_type === "both").map(bank => (
                  <SelectItem key={bank.id} value={bank.bank_code}>{bank.bank_code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Transaction ID</TableHead>
                <TableHead>Auth Code</TableHead>
                <TableHead>Flow</TableHead>
                <TableHead>Wallet Ref</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Fee</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Timestamp</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="animate-pulse text-muted-foreground">Loading transactions...</div>
                  </TableCell>
                </TableRow>
              ) : filteredTx.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <Receipt className="h-8 w-8 text-muted-foreground" />
                      <p className="text-muted-foreground">No transactions found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTx.map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell>
                      <code className="font-mono text-xs text-muted-foreground">
                        {tx.id.slice(0, 8)}...
                      </code>
                    </TableCell>
                    <TableCell>
                      <code className="font-mono text-sm bg-secondary px-2 py-1 rounded">
                        {(tx.authorization_code || tx.voucher_reference || tx.id).slice(0, 8)}...
                      </code>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <span className="text-primary">{tx.issuer_bank_code}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <span className="text-accent">{tx.acquirer_bank_code}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="font-mono text-xs text-muted-foreground">
                        {(() => {
                          const ref = tx.wallet_reference || tx.voucher_reference || '-';
                          return ref.length > 12 ? `${ref.slice(0, 12)}...` : ref;
                        })()}
                      </code>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono font-medium">{formatCurrency(tx.dispensed_amount)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-accent">{formatCurrency(tx.fee_applied)}</span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={tx.status} />
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(tx.timestamp)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Transactions;
